import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MdCheckCircle, MdHourglassTop, MdErrorOutline } from 'react-icons/md';
import API from '../api';
import { LanguageSelector, useLanguage } from '../i18n';

// Shown right after the payment request is sent to the customer's phone. It never
// trusts the URL: it asks our server, which asks the payment provider directly.
export default function BillingResult() {
    const { t } = useLanguage();
    const [params] = useSearchParams();
    const txRef = params.get('tx_ref') || '';
    const txId = params.get('transaction_id') || '';       // sent back by Flutterwave; only used to ask the server to check
    const urlStatus = params.get('status') || '';          // cancelled / failed when the customer backed out
    const [state, setState] = useState('checking'); // checking | successful | pending | failed | unknown
    const [gaveUp, setGaveUp] = useState(false);
    const tries = useRef(0);

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const check = async () => {
            tries.current += 1;
            try {
                const { data } = await API.get('/billing/verify', { params: { tx_ref: txRef, ...(txId ? { transaction_id: txId } : {}) } });
                if (cancelled) return;
                if (data.status === 'successful') { setState('successful'); return; }
                if (data.status === 'failed' || urlStatus === 'cancelled' || urlStatus === 'failed') { setState('failed'); return; }
                setState('pending');
            } catch (err) {
                if (cancelled) return;
                if (err.response?.status === 404) { setState('unknown'); return; }
                setState('pending');
            }
            if (tries.current < 30) timer = setTimeout(check, 4000); else setGaveUp(true);
        };
        if (!txRef) setState('unknown'); else check();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [txRef, txId, urlStatus]);

    const view = {
        checking: { icon: <MdHourglassTop size={44} color="#0b5ed7" />, title: t('billing.resultChecking'), body: t('billing.resultApprove') },
        pending: { icon: <MdHourglassTop size={44} color="#b45309" />, title: t('billing.resultChecking'), body: gaveUp ? t('billing.resultLate') : t('billing.resultApprove') },
        successful: { icon: <MdCheckCircle size={44} color="#15803d" />, title: t('billing.resultSuccess'), body: t('billing.resultSuccessBody') },
        failed: { icon: <MdErrorOutline size={44} color="#b91c1c" />, title: t('billing.resultFailed'), body: '' },
        unknown: { icon: <MdErrorOutline size={44} color="#b91c1c" />, title: t('billing.resultUnknown'), body: '' },
    }[state];

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px',
            background: 'linear-gradient(135deg, #edf5ff 0%, #dfeeff 30%, #b8d6ff 60%, #0b5ed7 100%)'
        }}>
            <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16, padding: 30, textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.12)' }}>
                <div style={{ textAlign: 'right' }}><LanguageSelector /></div>
                {view.icon}
                <h5 style={{ fontWeight: 700, margin: '14px 0 8px' }}>{view.title}</h5>
                {view.body && <p style={{ color: '#475569', fontSize: 14 }}>{view.body}</p>}
                {(gaveUp || (state !== 'checking' && state !== 'pending')) && (
                    <Link to="/app/billing" className="btn btn-primary mt-2">{t('billing.backToBilling')}</Link>
                )}
            </div>
        </div>
    );
}
